// media-skeleton.component.ts
import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-media-skeleton',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Image Message Skeleton -->
    <div *ngIf="type === 'image'" class="tw-max-w-sm tw-relative tw-group/bubble">
      <div class="tw-relative tw-overflow-hidden tw-rounded-2xl tw-shadow-lg">
        <!-- Image placeholder with shimmer effect -->
        <div class="tw-w-full tw-h-48 tw-bg-gradient-to-br tw-from-slate-200 tw-via-slate-300 tw-to-slate-400 dark:tw-from-slate-600 dark:tw-via-slate-700 dark:tw-to-slate-800 tw-relative tw-overflow-hidden">
          <div class="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/20 tw-to-transparent tw-translate-x-[-100%] tw-animate-shimmer"></div>
          <div class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
            <div class="tw-w-12 tw-h-12 tw-bg-slate-300/50 dark:tw-bg-slate-600/50 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
              <div class="tw-w-6 tw-h-6 tw-bg-slate-400/70 dark:tw-bg-slate-500/70 tw-rounded"></div>
            </div>
          </div>
        </div>
        
        <!-- Hover overlay skeleton -->
        <div class="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/10 tw-to-transparent"></div>
      </div>
      
      <!-- Caption skeleton -->
      <div *ngIf="showCaption" class="tw-mt-2">
        <div class="tw-h-4 tw-w-32 tw-bg-slate-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse"></div>
      </div>
      
      <!-- Filename skeleton -->
      <div *ngIf="showFilename" class="tw-mt-1">
        <div class="tw-h-3 tw-w-24 tw-bg-slate-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse"></div>
      </div>
    </div>

    <!-- File Message Skeleton -->
    <div *ngIf="type === 'file'" class="tw-relative tw-group/bubble">
      <div class="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-slate-50 dark:tw-bg-slate-800 tw-border tw-border-slate-200 dark:tw-border-slate-700 tw-rounded-2xl tw-max-w-sm">
        <div class="tw-flex-shrink-0">
          <div class="tw-w-12 tw-h-12 tw-bg-gradient-to-br tw-from-blue-100 tw-to-blue-200 dark:tw-from-blue-900/30 dark:tw-to-blue-800/30 tw-rounded-xl tw-animate-pulse">
            <div class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
              <div class="tw-w-6 tw-h-6 tw-bg-blue-300 dark:tw-bg-blue-600 tw-rounded tw-animate-pulse"></div>
            </div>
          </div>
        </div>
        <div class="tw-flex-1 tw-min-w-0 tw-space-y-2">
          <div class="tw-h-4 tw-w-32 tw-bg-slate-200 dark:tw-bg-slate-600 tw-rounded tw-animate-pulse"></div>
          <div class="tw-h-3 tw-w-16 tw-bg-slate-200 dark:tw-bg-slate-600 tw-rounded tw-animate-pulse"></div>
        </div>
        <div class="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-bg-slate-200 dark:tw-bg-slate-600 tw-rounded-lg tw-animate-pulse"></div>
      </div>
    </div>

    <!-- Video Message Skeleton -->
    <div *ngIf="type === 'video'" class="tw-max-w-sm tw-relative tw-group/bubble">
      <div class="tw-relative tw-overflow-hidden tw-rounded-2xl tw-shadow-lg tw-bg-black">
        <!-- Video placeholder -->
        <div class="tw-w-full tw-h-48 tw-bg-gradient-to-br tw-from-slate-800 tw-via-slate-900 tw-to-black tw-relative tw-overflow-hidden">
          <div class="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/10 tw-to-transparent tw-translate-x-[-100%] tw-animate-shimmer"></div>
          <!-- Play button skeleton -->
          <div class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
            <div class="tw-w-16 tw-h-16 tw-bg-white/20 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-animate-pulse">
              <div class="tw-w-0 tw-h-0 tw-border-l-[12px] tw-border-l-white/60 tw-border-y-[8px] tw-border-y-transparent tw-ml-1"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Video info skeleton -->
      <div class="tw-mt-2 tw-space-y-1">
        <div class="tw-h-4 tw-w-28 tw-bg-slate-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse"></div>
        <div class="tw-h-3 tw-w-20 tw-bg-slate-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse"></div>
      </div>
    </div>

    <!-- Audio Message Skeleton -->
    <div *ngIf="type === 'audio'" class="tw-relative tw-group/bubble tw-max-w-sm">
      <div class="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-slate-50 dark:tw-bg-slate-800 tw-border tw-border-slate-200 dark:tw-border-slate-700 tw-rounded-2xl">
        <!-- Play button skeleton -->
        <div class="tw-flex-shrink-0">
          <div class="tw-w-12 tw-h-12 tw-bg-gradient-to-br tw-from-purple-100 tw-to-purple-200 dark:tw-from-purple-900/30 dark:tw-to-purple-800/30 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-animate-pulse">
            <div class="tw-w-0 tw-h-0 tw-border-l-[8px] tw-border-l-purple-500 tw-border-y-[6px] tw-border-y-transparent tw-ml-1"></div>
          </div>
        </div>
        
        <!-- Audio waveform skeleton -->
        <div class="tw-flex-1 tw-min-w-0">
          <div class="tw-flex tw-items-center tw-gap-1 tw-mb-2">
            @for (item of [1,2,3,4,5,6,7,8]; track $index) {
              <div class="tw-w-1 tw-bg-slate-300 dark:tw-bg-slate-600 tw-rounded-full tw-animate-pulse"
                   [style.height.px]="getRandomHeight()"></div>
            }
          </div>
          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-h-3 tw-w-12 tw-bg-slate-200 dark:tw-bg-slate-600 tw-rounded tw-animate-pulse"></div>
            <div class="tw-h-3 tw-w-12 tw-bg-slate-200 dark:tw-bg-slate-600 tw-rounded tw-animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    
    .tw-animate-shimmer {
      animation: shimmer 2s infinite;
    }
  `]
})
export class MediaSkeletonComponent {
  @Input() type: 'image' | 'file' | 'video' | 'audio' = 'image';
  @Input() showCaption: boolean = false;
  @Input() showFilename: boolean = false;
  
  getRandomHeight(): number {
    return Math.floor(Math.random() * 20) + 10; // Random height between 10-30px
  }
}
